import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import {
  TheoryLevelResponse,
  TheoryLevelRequest,
  SkillDocumentResponse,
} from '../../../models/skill-learning.model';

const MIN_QUESTIONS_COUNT = 1;
const MAX_QUESTIONS_COUNT = 100;
const MIN_PASSING_PERCENTAGE = 1;
const MAX_PASSING_PERCENTAGE = 100;
const MIN_TIME_LIMIT_MINUTES = 1;
const MIN_DESCRIPTION_LENGTH = 50;
const DIRECTIONS_PROMPT_COPIED_KEY = 'SKILL_LEARNING_ADMIN_THEORY_PROMPT_COPIED';

type TheoryLevelInput = TheoryLevelResponse | null;
type SkillDocumentInput = SkillDocumentResponse | null;

export interface TheoryLevelSavePayload {
  request: TheoryLevelRequest;
  documentFile: File | null;
}

@Component({
  selector: 'app-skill-theory-level-editor',
  templateUrl: './theory-level-editor.component.html',
  styleUrls: ['./theory-level-editor.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkillTheoryLevelEditorComponent implements OnInit, OnChanges {

  @Input() theoryLevel: TheoryLevelInput = null;
  @Input() directionsPrompt: string = '';
  @Input() documentFileId: string | null = null;
  @Input() document: SkillDocumentInput = null;
  @Input() isSubmitting: boolean = false;

  @Output() levelSaved = new EventEmitter<TheoryLevelSavePayload>();
  @Output() documentDownloadRequested = new EventEmitter<void>();

  form: FormGroup;
  pendingDocument: File | null = null;
  copyStatusKey: string | null = null;

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.form = this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.theoryLevel && changes.theoryLevel.currentValue) {
      this.form = this.buildForm();
    }
  }

  get isFormInvalid(): boolean {
    return this.form.invalid || !this.hasRequiredDocument;
  }

  get hasRequiredDocument(): boolean {
    return this.hasPendingDocument || this.hasUploadedDocument;
  }

  get hasPendingDocument(): boolean {
    return !!this.pendingDocument;
  }

  get hasUploadedDocument(): boolean {
    if (this.hasDocumentMetadata()) {
      return true;
    }
    return this.hasDocumentIdentifier(this.documentFileId);
  }

  get descriptionLength(): number {
    const value: string = this.form.get('description').value || '';
    return value.length;
  }

  get minDescriptionLength(): number {
    return MIN_DESCRIPTION_LENGTH;
  }

  get canCopyDirectionsPrompt(): boolean {
    return (this.directionsPrompt || '').trim().length > 0;
  }

  onDocumentSelected(file: File | null): void {
    this.pendingDocument = file;
  }

  onDocumentDownload(): void {
    this.documentDownloadRequested.emit();
  }

  onCopyDirectionsPrompt(): void {
    if (!this.canCopyDirectionsPrompt) {
      return;
    }
    this.copyTextToClipboard(this.directionsPrompt);
    this.copyStatusKey = DIRECTIONS_PROMPT_COPIED_KEY;
  }

  onSave(): void {
    if (this.isFormInvalid) {
      return;
    }
    const request: TheoryLevelRequest = {
      description: this.form.get('description').value,
      topics: [],
      questionsCount: this.form.get('questionsCount').value,
      timeLimitMinutes: this.form.get('timeLimitMinutes').value,
      passingPercentage: this.form.get('passingPercentage').value,
    };
    this.levelSaved.emit({
      request,
      documentFile: this.pendingDocument,
    });
    this.pendingDocument = null;
  }

  private buildForm(): FormGroup {
    return this.fb.group({
      description: [
        this.theoryLevel ? this.theoryLevel.description : '',
        [Validators.required, Validators.minLength(MIN_DESCRIPTION_LENGTH)],
      ],
      questionsCount: [
        this.theoryLevel ? this.theoryLevel.questionsCount : MIN_QUESTIONS_COUNT,
        [Validators.required, Validators.min(MIN_QUESTIONS_COUNT), Validators.max(MAX_QUESTIONS_COUNT)],
      ],
      timeLimitMinutes: [
        this.theoryLevel ? this.theoryLevel.timeLimitMinutes : MIN_TIME_LIMIT_MINUTES,
        [Validators.required, Validators.min(MIN_TIME_LIMIT_MINUTES)],
      ],
      passingPercentage: [
        this.theoryLevel ? this.theoryLevel.passingPercentage : MIN_PASSING_PERCENTAGE,
        [Validators.required, Validators.min(MIN_PASSING_PERCENTAGE), Validators.max(MAX_PASSING_PERCENTAGE)],
      ],
    });
  }

  private hasDocumentMetadata(): boolean {
    if (!this.document) {
      return false;
    }
    return this.hasDocumentIdentifier(this.document.fileId);
  }

  private hasDocumentIdentifier(fileId: string | null): boolean {
    if (!fileId) {
      return false;
    }
    return fileId.trim().length > 0;
  }

  private copyTextToClipboard(text: string): void {
    const onCopy = (event: ClipboardEvent) => {
      event.clipboardData.setData('text/plain', text);
      event.preventDefault();
      document.removeEventListener('copy', onCopy);
    };
    document.addEventListener('copy', onCopy);
    document.execCommand('copy');
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/admin/theory-level-editor/theory-level-editor.component.ts